import { type FC } from 'react';
import { graphql, useFragment } from 'react-relay';
import { FormattedMessage, FormattedNumber } from 'dibs-react-intl';
import LazyImage from 'dibs-react-lazy-image';
import { useCurrency } from 'dibs-buyer-layout/exports/useCurrency';
import { Link } from 'dibs-elements/exports/Link';
import { useServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext';
import { SbSharedMarketTrendsQuestionWrapper } from './SbSharedMarketTrendsQuestionWrapper';
import { getPriceByCurrency } from './currencyHelpers';
import {
    VERTICAL_OFFSET_RESP_IMG,
    HORIZONTAL_OFFSET_RESP_IMG,
} from '../../constants/imageConstants';
import { getImageSrc } from 'dibs-buyer-layout/exports/imageHelpers';
import { trackEvent, eventNameConstants } from 'dibs-tracking';

import cn from 'classnames';
import dibsCss from 'dibs-css';
import styles from './SbSharedMarketTrendsTopSales.scss';

import { type SbSharedMarketTrendsTopSales_creatorMarketTrends$key } from './__generated__/SbSharedMarketTrendsTopSales_creatorMarketTrends.graphql';

const creatorMarketTrendsFragment = graphql`
    fragment SbSharedMarketTrendsTopSales_creatorMarketTrends on MarketTrendsConnection {
        priceStats {
            __typename
        }
        edges {
            node {
                rank
                itemId
                itemTitle
                itemThumbnail(size: thumb)
                pdpUrl
                salePrice {
                    ...currencyHelpers_financialAmount
                }
                dateSold
            }
        }
    }
`;

type Props = {
    creatorMarketTrends: SbSharedMarketTrendsTopSales_creatorMarketTrends$key;
    topic: string;
};

export const SbSharedMarketTrendsTopSales: FC<Props> = ({
    creatorMarketTrends: creatorMarketTrendsRef,
    topic,
}) => {
    const { isDesktop } = useServerVarsContext();
    const { currency } = useCurrency();
    const creatorMarketTrends = useFragment(creatorMarketTrendsFragment, creatorMarketTrendsRef);
    const { edges, priceStats } = creatorMarketTrends || {};

    if (!edges?.length) {
        return null;
    }

    return (
        <SbSharedMarketTrendsQuestionWrapper
            question={
                <FormattedMessage
                    id="abf.SbRespMarketTrends.topSales.question"
                    defaultMessage="{topic}: The 5 Most Expensive Sales on 1stDibs"
                    values={{ topic }}
                />
            }
            hasTopBorder={isDesktop || !!priceStats}
        >
            <p className={cn(dibsCss.sassyFontBodyMedium, dibsCss.mbMedium)}>
                <FormattedMessage
                    id="abf.SbRespMarketTrends.topSales.description"
                    defaultMessage="This list ranks the five highest prices achieved for {topic} sold through the 1stDibs marketplace, showcasing the most valuable examples to date."
                    values={{ topic }}
                />
            </p>
            <div className={styles.wrapper}>
                <table className={cn(styles.topSalesTable, dibsCss.textLeft, dibsCss.wFull)}>
                    <thead className={dibsCss.sassyFontBodySmallHeavy}>
                        <tr>
                            <th scope="col">
                                <FormattedMessage
                                    id="abf.SbRespMarketTrends.topSales.rank"
                                    defaultMessage="Rank"
                                />
                            </th>
                            <th scope="col">
                                <FormattedMessage
                                    id="abf.SbRespMarketTrends.topSales.item"
                                    defaultMessage="Item"
                                />
                            </th>
                            <th scope="col">
                                <FormattedMessage
                                    id="abf.SbRespMarketTrends.topSales.salePrice"
                                    defaultMessage="Sale Price"
                                />
                            </th>
                            <th scope="col">
                                <FormattedMessage
                                    id="abf.SbRespMarketTrends.topSales.dateSold"
                                    defaultMessage="Date Sold"
                                />
                            </th>
                        </tr>
                    </thead>
                    <tbody className={dibsCss.sassyFontBodyMedium}>
                        {edges.map(edge => {
                            const {
                                rank,
                                itemId,
                                itemTitle,
                                salePrice,
                                dateSold,
                                itemThumbnail,
                                pdpUrl,
                            } = edge?.node || {};
                            const itemThumbnailSrc = getImageSrc(itemThumbnail ?? '') ?? '';

                            return (
                                <tr key={itemId}>
                                    <td>
                                        <strong>{rank}</strong>
                                    </td>
                                    <td>
                                        <Link
                                            href={pdpUrl ?? ''}
                                            className={styles.itemLink}
                                            onClick={() => {
                                                trackEvent({
                                                    eventName:
                                                        eventNameConstants.EVENT_BROWSE_INTERACTION,
                                                    interaction_type:
                                                        'market trends - top sales click',
                                                });
                                            }}
                                        >
                                            <LazyImage
                                                offsetHorizontal={VERTICAL_OFFSET_RESP_IMG}
                                                offsetVertical={HORIZONTAL_OFFSET_RESP_IMG}
                                                placeholderClass={styles.itemThumbnail}
                                                placeholder={null}
                                            >
                                                <img
                                                    className={styles.itemThumbnail}
                                                    src={itemThumbnailSrc}
                                                    alt={itemTitle || ''}
                                                />
                                            </LazyImage>
                                            <span className={styles.itemTitle}>{itemTitle}</span>
                                        </Link>
                                    </td>
                                    <td>
                                        <strong>
                                            <FormattedNumber
                                                value={getPriceByCurrency(salePrice, currency) ?? 0}
                                                style="currency"
                                                currency={currency}
                                                maximumFractionDigits={0}
                                                minimumFractionDigits={0}
                                            />
                                        </strong>
                                    </td>
                                    <td>{dateSold}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </SbSharedMarketTrendsQuestionWrapper>
    );
};
